<template>
  <div>
    <!-- view settings -->
    <ProjectDetailControlsView
      :item="viewForSettings"
      :query-information="queryInformation"
      :dialog-status.sync="dialogStatus"
      :initial-tab="initialControlsTab"
      :initial-selected-roles="initialSelectedViewRoles"
      :users-initial="usersInitial"
      :users-to-delete="usersToDelete"
      :roles-to-delete="rolesToDelete"
      show-view-manager-link
      @controlsDialogOpened="closeViewsDropdown"
      @deleteView="deleteSelectedView"
      @viewUpdated="updateViewForSettings"
      @rolesChanged="computeRemovedRoles"
      @userRemoved="addUserToDelete" />
    <!-- new view copy -->
    <ProjectDetailViewCopy
      :views="views"
      :selected-view="viewToCopy"
      :dialog-status.sync="copyDialogStatus"
      @onCopy="onCopy"
      @updateCopiedView="setViewToCopy"
      @copyDialogOpened="closeViewsDropdown" />
    <!-- dropdown -->
    <v-select
      ref="viewsDropdown"
      v-model="selected"
      :items="views"
      :menu-props="{
        nudgeBottom: 42,
      }"
      color="black"
      :class="minimalVersion ? 'minimal-select' : 'sourcery__select'"
      item-text="name"
      dense
      full-width
      hide-details
      :outlined="!minimalVersion"
      :rounded="!minimalVersion"
      return-object>
      <template #append>
        <span
          v-if="!minimalVersion"
          class="sourcery__icon-wrapper black size-22">
          <img src="@/assets/icons/chevron-down-icon.svg">
        </span>
        <v-icon
          v-else
          color="lightBlack"
          :size="22">
          mdi-chevron-down
        </v-icon>
      </template>

      <template #selection="{ item }">
        <span
          class="fs-14"
          :class="{ 'text-uppercase' : !minimalVersion }">
          {{ item && item.name }}
        </span>
      </template>

      <!-- view manager + create new view btn -->
      <template #prepend-item>
        <v-tooltip
          :disabled="viewManagerAccess.allowed"
          bottom>
          <template #activator="{ on, attrs }">
            <v-list-item
              v-if="!isSharedLink"
              v-bind="attrs"
              class="green"
              @click="viewManagerAccess.allowed && openViewManager()"
              v-on="on">
              <v-list-item-title id="view-manager-item">
                View Manager
              </v-list-item-title>
            </v-list-item>
          </template>
          <span>
            {{ viewManagerAccess.reason }}
          </span>
        </v-tooltip>
        <v-list-item
          v-if="!isSharedLink"
          class="v-list-item--link blue white--text"
          @click.stop="showCopyPopup">
          {{ CREATE_NEW_VIEW }}
        </v-list-item>
      </template>

      <!-- item view -->
      <template #item="{ item, on }">
        <v-list-item
          :class="{'v-item--active v-list-item--active': item.name === selected.name }"
          v-on="on">
          <v-tooltip
            :disabled="showViewName(item.name).showTool"
            top>
            <template v-slot:activator="{ on, attrs }">
              <v-list-item-title
                v-bind="attrs"
                v-on="on">
                {{ showViewName(item.name).item }}
              </v-list-item-title>
              <div class="action-wrapper">
                <app-icon-btn
                  v-if="isDeletableView(item)"
                  :access="deleteViewAccess"
                  :show-tooltip="!deleteViewAccess || !deleteViewAccess.allowed"
                  icon-color="black"
                  icon-height="20px"
                  icon-name="mdi-delete"
                  bottom
                  @click.stop="deleteViewAccess.allowed && deleteSelectedView(item)" />
                <app-icon-btn
                  :access="viewSettingsAccess"
                  :show-tooltip="!viewSettingsAccess || !viewSettingsAccess.allowed"
                  class="d-flex align-self-center"
                  icon-color="black"
                  icon-height="20px"
                  icon-name="mdi-settings"
                  bottom
                  @click.stop="viewSettingsAccess.allowed && showViewSettingsDialog(item)" />
              </div>
            </template>
            <span>
              {{ item.name || item }}
            </span>
          </v-tooltip>
        </v-list-item>
      </template>
    </v-select>
  </div>
</template>

<script>
import {
  mapState,
  mapGetters,
  mapMutations,
  mapActions,
} from 'vuex';
import ProjectDetailControlsView from './ProjectDetailControlsView';
import ProjectDetailViewCopy from './ProjectDetailViewCopy';
import { ProjectViewsControlsMixin } from '@/mixins/ProjectViewsControlsMixin';
import { CREATE_NEW_VIEW } from '@/constants/scheduleViews';
import {
  EventBus,
  OPEN_VIEW_MANAGER,
} from '@/utils/eventBus';
export default {
  name: 'ProjectDetailsViews',
  components: {
    ProjectDetailControlsView,
    ProjectDetailViewCopy,
  },
  mixins: [
    ProjectViewsControlsMixin,
  ],
  props: {
    isSharedLink: {
      type: Boolean,
      default: false,
    },
    // Use in ProjectDetailsExpandedView
    minimalVersion: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      CREATE_NEW_VIEW,
      copyDialogStatus: false,
      dialogStatus: false,
      syncTime: null,
    };
  },
  computed: {
    ...mapState(['role']),
    ...mapGetters('UserRoles', ['canModifyViews']),
    selected: {
      get() {
        return this.selectedView;
      },
      set(val) {
        clearTimeout(this.syncTime);
        if (this.selectedView?.id !== val?.id) {
          this.setUploadingSpinner(true);
          this.syncTime = setTimeout(() => {
            if (this.selectedView?.id !== val?.id) {
              this.setViewId(val);
            }
          }, 10);
        }
      },
    },
    viewManagerAccess() {
      return this.canModifyViews(this.role);
    },
  },
  methods: {
    ...mapActions({
      handleError: 'handleError',
    }),
    ...mapMutations({
      spinner: 'spinner',
      setUploadingSpinner: 'setUploadingSpinner',
    }),
    async showViewSettingsDialog(item, initialControlsTab) {
      const res = await this.loadViewSettingsData(item, initialControlsTab);
      if (res) this.dialogStatus = true;
    },
    showCopyPopup() {
      this.spinner(true);
      try {
        this.copyDialogStatus = true;
      } catch (err) {
        this.handleError(err);
      } finally {
        this.spinner(false);
      }
    },
    async onCopy() {
      const item = await this.copyView();
      this.closeViewsDropdown();
      this.showViewSettingsDialog(item);
    },
    async deleteSelectedView(view) {
      await this.deleteView(view);
      this.closeViewsDropdown();
    },
    showViewName(val) {
      if (val && val.length > 30) {
        const item = `${val.substr(0, 30)}...`;
        return {
          item,
          showTool: false,
        };
      } else {
        return {
          item: val,
          showTool: true,
        };
      }
    },
    isDeletableView(item) {
      return this.viewIsDeletable(item);
    },
    closeViewsDropdown() {
      this.$refs.viewsDropdown.isMenuActive = false;
      this.$refs.viewsDropdown.blur();
    },
    openViewManager() {
      EventBus.$emit(OPEN_VIEW_MANAGER);
    },
  },
};
</script>

<style scoped lang="scss">
.minimal-select {
  margin-top: 0px !important;

  ::v-deep .v-input__slot::before {
    border-width: 0;
  }
  ::v-deep .v-input__slot::after {
    border-width: 0;
  }
}
.action-wrapper {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 20px;
}
.create-new-view {
  position: sticky;
  top: 0;
  background-color: var(--v-blue-base);
  z-index: 1;
}
// Specificty shenanigans
#view-manager-item {
  color: white !important;
}
</style>
